import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { scheduleApi, agentApi } from "../../api/client";
import { Button, Badge, Modal } from "../../components/ui";
import PageHeader from "../../components/PageHeader";

const STATUS_COLORS = { active: COLORS.green, paused: COLORS.orange, completed: COLORS.textMuted };
const OUTPUT_ICONS = { log: "\uD83D\uDCCB", email: "\u2709", webhook: "\uD83D\uDD17" };

// Exported for unit testing \u2014 relative-time label from an ISO timestamp.
export const timeAgo = (iso) => {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// Exported for unit testing \u2014 agent display name from the loaded list,
// falling back to a short id slice, then an em-dash.
export const resolveAgentName = (agents, id) =>
  (agents || []).find(a => a.id === id)?.name || id?.slice(0, 8) || "\u2014";

// Exported for unit testing \u2014 the create button is disabled until every
// required field is present. Mirrors the button's disabled predicate.
export const isCreateDisabled = (form, submitting) =>
  !!submitting || !form.name.trim() || !form.agent_id || !form.cron_expression || !form.message.trim();

// Local Banner — mirrors the pattern in KnowledgeLibraryPanel + the
// other Knowledge tabs. No shared component today; keep visual + DX
// consistent with what's already there rather than introduce a new
// abstraction in this PR.
const Banner = ({ kind, children, onDismiss }) => {
  const color = kind === "error" ? COLORS.pink : COLORS.green;
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8,
      border: `1px solid ${color}55`, background: `${color}18`,
      color: COLORS.text, fontSize: 12,
      display: "flex", justifyContent: "space-between", gap: 12,
      marginBottom: 12,
    }}>
      <span>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: "transparent", border: "none", color: COLORS.textDim,
          cursor: "pointer", fontSize: 14,
        }}>{"\u2715"}</button>
      )}
    </div>
  );
};

const SchedulesView = () => {
  const bp = useBreakpoint();
  const [jobs, setJobs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  // Bug #5 fix (2026-05-14) — surface success + error on mutations.
  // Pre-fix: `handleCreate` closed the modal silently on success and
  // had an empty `catch {}` swallowing every failure, so users had
  // no signal whether the create worked. Backend POST /api/schedules
  // returned 201, frontend refetched, but the user saw nothing
  // confirming "schedule created" → reported as "unable to create".
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "", agent_id: "", cron_expression: "", message: "",
    output_action: "log", output_config: {},
  });

  const fetchJobs = async () => {
    setLoading(true);
    try { const r = await scheduleApi.list(); if (r.success) setJobs(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    fetchJobs();
    agentApi.list().then(r => { if (r.success) setAgents(r.data || []); }).catch(() => {});
    scheduleApi.presets().then(r => { if (r.success) setPresets(r.data || []); }).catch(() => {});
  }, []);

  // Auto-dismiss success banner after 4s. Errors stay until the user
  // acknowledges (they need to read + act on the message).
  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => setInfo(null), 4000);
    return () => clearTimeout(t);
  }, [info]);

  const handleCreate = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const res = await scheduleApi.create(form);
      if (res?.success) {
        const created = res?.data?.name || form.name || "Schedule";
        setShowCreate(false);
        setForm({ name: "", agent_id: "", cron_expression: "", message: "", output_action: "log", output_config: {} });
        await fetchJobs();
        setInfo(`Schedule "${created}" created.`);
      } else {
        // request() helper resolves with the parsed body on non-2xx
        // when the body shape is parseable. Surface whatever detail
        // we have rather than swallowing.
        const detail = res?.error || res?.detail || res?.message || "Create failed.";
        setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
    } catch (e) {
      // Empty catch was the original silent-failure path. Now surface
      // the message; common cases: 400 Invalid cron, 412 missing
      // config, 422 pydantic validation, network error.
      setErr(e?.message || String(e) || "Create failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const agentName = (id) => resolveAgentName(agents, id);

  return (
    <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>
      <PageHeader
        title="Schedules"
        subtitle="Cron-based agent runs with automated output routing"
        primaryAction={{
          label: "New Schedule",
          iconName: "action.add",
          onClick: () => setShowCreate(true),
          style: { background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`, border: "none", color: "#fff" },
        }}
      />

      {/* Bug #5 fix — success / error feedback on mutations. Banners
          live above the list so the user sees outcome without
          scrolling, and dismiss-able so they don't clutter on long
          sessions. */}
      {info && <Banner kind="success" onDismiss={() => setInfo(null)}>{info}</Banner>}
      {err && <Banner kind="error" onDismiss={() => setErr(null)}>{err}</Banner>}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim }}>Loading...</div>
      ) : jobs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.textDim }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{"\u23F0"}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textMuted }}>No scheduled workflows</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Create a schedule to run agents automatically on a cron schedule</div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {jobs.map((j, idx) => (
            // i2-visual-identity-v2 §10.2 B1 — Schedules row card.
            // Adopts the AgentTile-equivalent: solid surface +
            // spatialShadow + RADIUS.inner + cascade entry + spring
            // hover. Same primitive set the Agents page uses, scaled
            // for list-row layout.
            <motion.div
              key={j.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring", stiffness: 400, damping: 25,
                delay: Math.min(idx * 0.05, 0.4),
              }}
              whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 25 } }}
              onClick={() => { setShowDetail(j.id); setDetailData(j); }}
              style={{
                padding: 16,
                borderRadius: RADIUS.inner,
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                boxShadow: MATERIALS.spatialShadow(),
                cursor: "pointer",
              }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{"\u23F0"}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{j.name}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>
                      {agentName(j.agent_id)} {"\u00B7"} <code style={{ fontSize: 10, color: COLORS.cyan }}>{j.cron_expression}</code>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 12 }}>{OUTPUT_ICONS[j.output_action]}</span>
                  <Badge color={STATUS_COLORS[j.status] || COLORS.textMuted}>{j.status}</Badge>
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 11, color: COLORS.textMuted }}>
                <span>Runs: {j.run_count}</span>
                <span>Last: {timeAgo(j.last_run_at)}</span>
                <span title={j.next_run_at ? `In schedule timezone: ${new Date(j.next_run_at).toLocaleString("en-US", { timeZone: j.timezone || "UTC", timeZoneName: "short" })}` : ""}>
                  Next: {j.next_run_at ? new Date(j.next_run_at).toLocaleString("en-US", { timeZone: j.timezone || "UTC", timeZoneName: "short" }) : "—"}
                </span>
                {j.last_status && <Badge color={j.last_status === "success" ? COLORS.green : COLORS.pink}>{j.last_status}</Badge>}
              </div>
              {/* Actions */}
              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                {j.status === "active" && (
                  <>
                    <Button size="sm" onClick={async e => { e.stopPropagation(); await scheduleApi.runNow(j.id); setTimeout(fetchJobs, 2000); }}
                      style={{ background: `${COLORS.accent}15`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30` }}>
                      Run Now
                    </Button>
                    <Button size="sm" onClick={async e => { e.stopPropagation(); await scheduleApi.pause(j.id); fetchJobs(); }}
                      style={{ color: COLORS.orange }}>Pause</Button>
                  </>
                )}
                {j.status === "paused" && (
                  <Button size="sm" onClick={async e => { e.stopPropagation(); await scheduleApi.resume(j.id); fetchJobs(); }}
                    style={{ background: `${COLORS.green}15`, color: COLORS.green, border: `1px solid ${COLORS.green}30` }}>Resume</Button>
                )}
                <Button size="sm" variant="secondary" onClick={async e => { e.stopPropagation(); await scheduleApi.delete(j.id); fetchJobs(); }}
                  style={{ color: COLORS.pink }}>Delete</Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} title="New Scheduled Workflow" onClose={() => setShowCreate(false)}>
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Name</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Daily Sales Report"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13 }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Agent</label>
            <select value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }}>
              <option value="">Select agent...</option>
              {agents
                .filter(a => a.status !== "archived")
                .map(a => {
                  const s = a.status || "live";
                  const isLive = s === "live" || s === "active";
                  return <option key={a.id} value={a.id}>{a.name}{!isLive ? ` (${s})` : ""}</option>;
                })}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Schedule (cron)</label>
            <input type="text" value={form.cron_expression} onChange={e => setForm(f => ({ ...f, cron_expression: e.target.value }))}
              placeholder="e.g. 0 9 * * 1-5"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13, fontFamily: "monospace" }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {presets.map(p => (
                <button key={p.cron} onClick={() => setForm(f => ({ ...f, cron_expression: p.cron }))}
                  style={{
                    padding: "3px 8px", borderRadius: 6, fontSize: 10, cursor: "pointer",
                    border: `1px solid ${form.cron_expression === p.cron ? COLORS.accent : COLORS.border}`,
                    background: form.cron_expression === p.cron ? COLORS.accentSoft : COLORS.surface,
                    color: form.cron_expression === p.cron ? COLORS.accent : COLORS.textMuted,
                  }}>{p.label}</button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Message (prompt for the agent)</label>
            <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="e.g. Generate a summary of yesterday's conversations and key metrics"
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Output</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ id: "log", label: "Log Only", icon: "\uD83D\uDCCB" }, { id: "email", label: "Email", icon: "\u2709" }, { id: "webhook", label: "Webhook", icon: "\uD83D\uDD17" }].map(o => (
                <Button key={o.id} size="sm" variant={form.output_action === o.id ? "primary" : "secondary"}
                  onClick={() => setForm(f => ({ ...f, output_action: o.id }))}
                  style={form.output_action === o.id ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}` } : {}}>
                  {o.icon} {o.label}
                </Button>
              ))}
            </div>
          </div>

          {form.output_action === "email" && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Email To</label>
              <input type="email" value={form.output_config.email_to || ""} onChange={e => setForm(f => ({ ...f, output_config: { ...f.output_config, email_to: e.target.value } }))}
                placeholder="report@company.com"
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
            </div>
          )}

          {form.output_action === "webhook" && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Webhook URL</label>
              <input type="url" value={form.output_config.webhook_url || ""} onChange={e => setForm(f => ({ ...f, output_config: { ...f.output_config, webhook_url: e.target.value } }))}
                placeholder="https://hooks.slack.com/..."
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
            </div>
          )}

          <button onClick={handleCreate}
            disabled={isCreateDisabled(form, submitting)}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
              background: (submitting || !form.name.trim() || !form.agent_id)
                ? COLORS.textDim
                : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
              color: "#fff", fontSize: 14, fontWeight: 600,
              cursor: (submitting || !form.name.trim()) ? "not-allowed" : "pointer",
              opacity: submitting ? 0.75 : 1,
            }}>{submitting ? "Creating\u2026" : "Create Schedule"}</button>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal open={!!showDetail} title={detailData?.name || "Schedule Details"} onClose={() => { setShowDetail(null); setDetailData(null); }}>
        {detailData && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
              {[
                { label: "Runs", value: detailData.run_count },
                { label: "Status", value: detailData.last_status || "—", color: detailData.last_status === "success" ? COLORS.green : detailData.last_status === "error" ? COLORS.pink : COLORS.text },
                { label: "Next Run", value: detailData.next_run_at ? new Date(detailData.next_run_at).toLocaleString("en-US", { timeZone: detailData.timezone || "UTC", timeZoneName: "short" }) : "—" },
              ].map(s => (
                <div key={s.label} style={{ padding: 12, borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.color || COLORS.text }}>{s.value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Cron</div>
              <code style={{ fontSize: 13, color: COLORS.cyan }}>{detailData.cron_expression}</code>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Agent</div>
              <div style={{ fontSize: 13, color: COLORS.text }}>{agentName(detailData.agent_id)}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Message</div>
              <div style={{ fontSize: 12, color: COLORS.text, padding: 10, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>{detailData.message}</div>
            </div>
            {detailData.last_result && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Last Result</div>
                <div style={{ fontSize: 12, color: COLORS.text, padding: 10, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, maxHeight: 200, overflowY: "auto", whiteSpace: "pre-wrap" }}>{detailData.last_result}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SchedulesView;
